import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'
import { revealOnLoad, revealOnScroll } from 'helpers/reveal-item'
import { gsapBreakpoints } from 'utility/breakpoints'

const loader = {
  el: {
    alerts: document.querySelector<HTMLElement>('.alerts'),
    annualReport: document.querySelector<HTMLElement>('.main-ar'),
    articlePage: document.querySelector<HTMLElement>('.article-page'),
    articlesPage: document.querySelector<HTMLElement>('.articles-page'),
    articlesPageSelect: document.querySelector<HTMLElement>('.articles-page__select select'),
    errorPage: document.querySelector<HTMLElement>('.error-page'),
    formsPage: document.querySelector<HTMLElement>('.forms-page'),
    formsPageSelect: document.querySelector<HTMLElement>('.forms-page__select select'),
    imageCards: document.querySelector<HTMLElement>('.image-card'),
    links: document.querySelectorAll<HTMLElement>(`
      .alert__link,
      .article-link-item,
      .article-page__back-button,
      .articles-page__pagination .pagination__button,
      .department,
      .department-shortcuts-block__link,
      .forms-page__button,
      .header__buttons .button,
      .header__logo,
      .header-navigation__link,
      .image-card__card,
      .latest-articles-block__button,
      .mobile-header__contact-button,
      .mobile-header__logo,
      .mobile-navigation__link,
      .search-page__suggested-words .button,
      .search-page__category,
      .search-page .pagination__button,
      .subnavigation__link,
      .subnavigation-container__level-1-button,
      .subnavigation-container__a-b-button
    `),
    pageCover: document.querySelector<HTMLElement>('.page-cover'),
    searchForm: document.querySelector<HTMLElement>('.search-page__search-form'),
    searchPage: document.querySelector<HTMLElement>('.search-page'),
  },

  selectors: {
    blocks: `
      .alerts,

      .banner__subtitle,
      .banner__title,
      .banner__leading-paragraph,
      .banner__text,
      .banner__buttons .button,
      .banner__picture,
      .banner .departments__title,
      .banner .departments__items .department,
      .banner .departments__subtitle,
      .banner .departments__text-container,
      .banner__bullet-box-numbers,
      .banner .bullet-box,
      .banner .bullet-box__list li,
      .banner .bullet-box__buttons .button,
      .banner .key-number,

      .calculator-95-block > .container,

      .accordion-block__title,
      .accordion-block__leading-paragraph,
      .accordion-block__text h3,
      .accordion-block__text h4,
      .accordion-block__text p,
      .accordion-block__text ul,
      .accordion-block__text ol,
      .accordion-block .accordion__item-wrapper,

      .calculator-block,

      .chart-block__title,
      .chart-block__leading-paragraph,
      .chart-block__text h3,
      .chart-block__text h4,
      .chart-block__text p,
      .chart-block__text ul,
      .chart-block__text ol,
      .chart-block__embed,

      .comparison-cards-block__title,
      .comparison-card,
      .comparison-card__title,
      .comparison-card__list li,
      .comparison-card__text,
      .comparison-card__button-container,

      .comparison-table-block__title,
      .comparison-table-block__text h3,
      .comparison-table-block__text h4,
      .comparison-table-block__text p,
      .comparison-table-block__text ul,
      .comparison-table-block__text ol,
      .comparison-table-block__buttons,
      .comparison-table-block__column--table,
      .comparison-table-block__column--table-background,

      .department-shortcuts-block__title,
      .department-shortcuts-block__box,
      .department-shortcuts-block__list li,

      .employee-block__title,
      .employees-block__tables .table-block,

      .example-block,
      .example-block__text h3,
      .example-block__text h4,
      .example-block__text p,
      .example-block__text ul,
      .example-block__text ol,

      .faq-block__title,
      .faq-block__buttons,
      .faq-block__select,
      .faq-block__accordions .accordion__item-wrapper,
      .faq-block__bottom,

      .full-width-image-block img,
      .full-width-image-block__caption,

      .grading-block > .container,

      .grading-progress-block > .container,

      .image-card__card,
      
      .icon-cards-block__card,

      .image-block__picture,
      .image-block__caption,

      .information-block__title,
      .information-block__leading-paragraph,
      .information-block__text p,
      .information-block__text h3,
      .information-block__text h4,
      .information-block__text ul,
      .information-block__text ol,
      .information-block__buttons,
      .information-block__picture,
      .information-block .bullet-box,
      .information-block .bullet-box__list li,
      .information-block .bullet-box__buttons .button,

      .key-information-block__title,
      .key-information-block__text p,
      .key-information-block__text h3,
      .key-information-block__text h4,
      .key-information-block__text ul,
      .key-information-block__text ol,
      .key-information-block .key-information__item,

      .key-numbers-block__subtitle,
      .key-numbers-block__title,
      .key-numbers-block__text,
      .key-numbers-block__button,
      .key-numbers-block .key-number,

      .latest-articles-block__title,
      .latest-articles-block .article-link-item,
      .latest-articles-block__button,

      .life-steps-block__title,
      .life-steps-block__text p,
      .life-steps-block__text h3,
      .life-steps-block__text h4,
      .life-steps-block__text ul,
      .life-steps-block__text ol,
      .life-steps-block__button,
      .life-steps-block__accordion .accordion__item-wrapper,

      .number-cards-block__card,

      .numbers-block__title,
      .numbers-block__leading-paragraph,
      .numbers-block__text p,
      .numbers-block__text h3,
      .numbers-block__text h4,
      .numbers-block__text ul,
      .numbers-block__text ol,
      .numbers-block__button,
      .numbers-block__numbers-box,

      .shortcuts-block__title,
      .shortcuts-block__box,

      .slider-block__subtitle,
      .slider-block__title,
      .slider-block__text,
      .slider-block__button,
      .slider-block__arrow-buttons,
      .slider-block__items,
      .slider-block__pagination,

      .table-block__title,
      .table-block__text,
      .table-block__column--table,
      .table-block__column--table-background,

      .text-block__title,
      .text-block__subtitle,
      .text-block__leading-paragraph,
      .text-block__text h3,
      .text-block__text h4,
      .text-block__text p,
      .text-block__text p img,
      .text-block__text ul,
      .text-block__text ol,
      .text-block__button,

      .article-page__back-button,
      .article-page__title,
      .article-page__date,
      .article-page__summary,
      .article-page__picture,

      .contact-page__title,
      .contact-page__text,
      .contact-page__form .input-group,
      .contact-page__submit-button,
      .contact-page__recaptcha-disclaimer,

      .video-block
    `,
    articlesPage: `
      .articles-page__title,
      .articles-page__select,
      .articles-page .article-link-item,
      .articles-page__pagination
    `,
    errorPage: `
      .error-page__404,
      .error-page__title,
      .error-page__text,
      .error-page__button
    `,
    formsPage: `
      .forms-page__title,
      .forms-page__buttons,
      .forms-page__select,
      .forms-page__forms .forms-page__subtitle,
      .forms-page__forms .form-item,
      .example-block
    `,
    searchPage: `
      .search-page__title,
      .search-page__search-form,
      .search-page__suggested-words-title,
      .search-page__suggested-words,
      .search-page__num-results-title,
      .search-page__categories-container,
      .search-page__result,
      .search-page__result-items .employees-block__tables,
      .search-page__result-items .form-item,
      .search-page .pagination
    `,
    annualReport: `
      .ar-subchapter-block__title,
      .ar-subchapter-block__line,

      .frontpage-highlight__heading,
      .frontpage-highlight__title,
      .frontpage-highlight__text,
      .frontpage-highlight__button,
      .frontpage-highlight__background-icon,
      .frontpage-highlight__picture,

      .ar-chapters .image-card,

      .statistics-block__title,
      .statistics-block__item,

      .statistics-columns-block__title,
      .statistics-columns-block__column,
      .statistic-item__title,
      .statistic-item__value-title,
      .statistic-item__extra-value-val,
      .statistic-item__label,

      .embed-item__title,
      .embed-item__subtitle,
      .embed-item__embed,

      .text-block__title,
      .text-block__subtitle,
      .text-block__leading-paragraph,
      .text-block__text h3,
      .text-block__text h4,
      .text-block__text p,
      .text-block__text p img,
      .text-block__text ul,
      .text-block__text ol,
      .text-block__button,

      .quote-block__quote,

      .member-list-block__title,
      .member-list-block__member-name,
      .member-list-block__member-title,
      .member-list-block__member-description,

      .ar-image-block__picture,
      .ar-image-block__caption,

      .ar-table-block__title,
      .ar-table-block__column,
      .ar-table-block__subtitle,

      .triple-image-block__picture,

      .icon-text-box-block__column,
      .icon-text-box-block__icon,
      .icon-text-box-block__title,
      .icon-text-box-block__text,

      .icon-list-column-block,
      .icon-text-columns__column,
      .icon-text-columns__item,
      .icon-list-column-block__legend,

      .rating-block,
      .rating-block__item-title,
      .rating-block__item-score,
      .rating-block__total

    `,
  },

  init(): void {
    gsap.registerPlugin(ScrollTrigger)

    // Handling page load
    if (!sessionStorage.getItem('pageLoaded')) {
      // First page load
      this.animatePageCover()
      this.animateOnPageEnter(0.6)
    } else {
      // Subsequent page loads
      this.el.pageCover.remove()
      this.animateOnPageEnter(0)
    }

    // Handling button & link clicks
    this.el.links.forEach((link) => {
      link.addEventListener('click', (event) => {
        // annual report navigation
        if (
          link.classList.contains('mobile-navigation-ar__link') &&
          link.parentElement.classList.contains('mobile-navigation__item--has-subnavigation')
        ) {
          return
        }
        if (link.getAttribute('target') !== '_blank') {
          event.preventDefault()
          this.handleActiveLink(link)
          this.animateOnPageLeave(link)
        }
      })
    })

    // Handling select changes on articles page
    if (this.el.articlesPage) {
      this.el.articlesPageSelect.addEventListener('change', () => {
        sessionStorage.setItem('buttonClicked', 'articlesSelect')
        this.animateOnPageLeave(this.el.articlesPageSelect)
      })
    }

    // Handling select changes on forms page
    if (this.el.formsPage) {
      this.el.formsPageSelect.addEventListener('change', () => {
        sessionStorage.setItem('buttonClicked', 'formsPage')
        this.animateOnPageLeave(this.el.formsPageSelect)
      })
    }

    // Handling form submit on search page
    if (this.el.searchForm) {
      this.el.searchForm.addEventListener('submit', () => {
        this.animateOnPageLeave(this.el.searchForm)
      })
    }
  },

  animateOnPageEnter(delay): void {
    // Articles page reveal
    if (this.el.articlesPage) {
      // When changing years in the select
      if (sessionStorage.getItem('buttonClicked') === 'articlesSelect') {
        revealOnLoad({
          callback: () => {
            document.querySelectorAll(this.selectors.articlesPage).forEach((item) => {
              revealOnScroll({ item })
            })
          },
          delay,
          selectors: '.article-link-item, .articles-page__pagination',
        })
        // Default articles page reveal
      } else {
        revealOnLoad({
          callback: () => {
            document.querySelectorAll(this.selectors.articlesPage).forEach((item) => {
              revealOnScroll({ item })
            })
          },
          delay,
          selectors: this.selectors.articlesPage,
        })
      }

      sessionStorage.removeItem('buttonClicked')
      return
    }

    // Error page reveal
    if (this.el.errorPage) {
      revealOnLoad({ delay, selectors: this.selectors.errorPage })
      return
    }

    // Forms page reveal
    if (this.el.formsPage) {
      // When previous page was forms page
      if (sessionStorage.getItem('buttonClicked') === 'formsPage') {
        revealOnLoad({
          callback: () => {
            document.querySelectorAll(this.selectors.formsPage).forEach((item) => {
              revealOnScroll({ item })
            })
          },
          delay,
          selectors: '.forms-page__forms .forms-page__subtitle, .forms-page__forms .form-item',
        })
        // Default forms page reveal
      } else {
        revealOnLoad({
          callback: () => {
            document.querySelectorAll(this.selectors.formsPage).forEach((item) => {
              revealOnScroll({ item })
            })
          },
          delay,
          selectors: this.selectors.formsPage,
        })
      }

      sessionStorage.removeItem('buttonClicked')
      return
    }

    // Search page reveal
    if (this.el.searchPage) {
      // When using suggested words buttons
      if (sessionStorage.getItem('buttonClicked') === 'searchSuggestedWord') {
        revealOnLoad({
          callback: () => {
            document.querySelectorAll(this.selectors.searchPage).forEach((item) => {
              revealOnScroll({ item })
            })
          },
          delay,
          selectors: '.search-page__num-results-title, .search-page__categories-container, .search-page__result, .search-page .pagination',
        })
        // When using category or pagination buttons
      } else if (sessionStorage.getItem('buttonClicked') === 'searchCategory') {
        revealOnLoad({
          callback: () => {
            document.querySelectorAll(this.selectors.searchPage).forEach((item) => {
              revealOnScroll({ item })
            })
          },
          delay,
          selectors:
            '.search-page__result, .search-page__result-items .employees-block__tables, .search-page__result-items .form-item, .search-page .pagination',
        })
        // When submitting the search form
      } else if (sessionStorage.getItem('buttonClicked') === 'searchSubmit') {
        revealOnLoad({
          callback: () => {
            document.querySelectorAll(this.selectors.searchPage).forEach((item) => {
              revealOnScroll({ item })
            })
          },
          delay,
          selectors:
            '.search-page__result, .search-page__result-items .employees-block__tables, .search-page__result-items .form-item, .search-page .pagination',
        })
        // Default search page reveal
      } else {
        revealOnLoad({
          callback: () => {
            document.querySelectorAll(this.selectors.searchPage).forEach((item) => {
              revealOnScroll({ item })
            })
          },
          delay,
          selectors: this.selectors.searchPage,
        })
      }

      sessionStorage.removeItem('buttonClicked')
      return
    }

    // Annual Report page reveal
    if (this.el.annualReport) {
      this.handleAnnualReportReveal(delay)

      return
    }

    // Default reveal of all the blocks
    revealOnLoad({
      callback: () => {
        document.querySelectorAll(this.selectors.blocks).forEach((item) => {
          revealOnScroll({ item })
        })
      },
      delay,
      selectors: this.selectors.blocks,
    })

    // Additional animation for frontpage hero reveal
    if (this.el.imageCards) {
      gsap.set('.image-card__card img', { rotation: 4, scale: 1.2, transition: 'none' })
      gsap.to('.image-card__card img', {
        clearProps: 'all',
        delay,
        duration: (index) => 2.1 + index * 0.3,
        ease: 'power3.out',
        rotation: 0,
        scale: 1.02,
      })
    }
  },

  animateOnPageLeave(link): void {
    // Articles page out animation when using the select
    if (link.closest('.articles-page__select')) {
      sessionStorage.setItem('buttonClicked', 'articlesSelect')
      return
    }

    // Articles page out animation when using pagination buttons
    if (link.closest('.articles-page__pagination')) {
      sessionStorage.setItem('buttonClicked', 'articlesSelect')
      this.fadeOut('.articles-page__items, .articles-page__pagination')
      this.redirect(link)
      return
    }

    // Forms page out animation when using category buttons
    if (link.classList.contains('forms-page__button')) {
      sessionStorage.setItem('buttonClicked', 'formsPage')
      this.fadeOut('.forms-page__forms')
      this.redirect(link)
      return
    }

    // Forms page out animation when using the select
    if (link.closest('.forms-page__select')) {
      sessionStorage.setItem('buttonClicked', 'formsPage')
      return
    }

    // Search page out animation when using suggested words buttons
    if (link.parentElement.classList.contains('search-page__suggested-words')) {
      sessionStorage.setItem('buttonClicked', 'searchSuggestedWord')
      this.fadeOut('.search-page__suggested-words-container')
      this.redirect(link)
      return
    }

    // Search page out animation when using category buttons or pagination
    if (link.classList.contains('search-page__category') || link.classList.contains('pagination__button')) {
      sessionStorage.setItem('buttonClicked', 'searchCategory')
      this.fadeOut('.search-page__result-items, .search-page .pagination')
      this.redirect(link)
      return
    }

    // Search page out when submitting the form
    if (link.classList.contains('search-page__search-form')) {
      sessionStorage.setItem('buttonClicked', 'searchSubmit')
      return
    }

    if (link.classList.contains('mobile-navigation-ar__link')) {
      if (!link.getAttribute('href')) {
        this.redirect(link.parentElement)
      } else {
        this.redirect(link)
      }
      return
    }

    // Default page out animation
    this.fadeOut('.main > section:not(.subnavigation-container)')
    this.redirect(link)
  },

  animatePageCover(): void {
    sessionStorage.setItem('pageLoaded', 'true')

    gsap.to(this.el.pageCover, {
      delay: 0.543,
      duration: 1.234,
      ease: 'power4.out',
      opacity: 0,
      onComplete: () => {
        this.el.pageCover.remove()
      },
    })
  },

  fadeOut(selectors): void {
    gsap.to(selectors, { delay: 0.1, duration: 0.1, opacity: 0 })
  },

  handleActiveLink(link): void {
    if (link.classList.contains('forms-page__button')) {
      link
        .closest('.forms-page__buttons')
        .querySelectorAll('.button')
        .forEach((link) => {
          link.classList.remove('button--default')
          link.classList.add('button--secondary')
        })
      link.classList.remove('button--secondary')
      link.classList.add('button--default')
    }

    if (
      link.classList.contains('header__logo') ||
      link.classList.contains('header__my-pages-button') ||
      link.classList.contains('subnavigation-container__level-1-button')
    ) {
      document.querySelectorAll('.header-navigation__link').forEach((link) => link.classList.remove('header-navigation__link--active'))
      document.querySelectorAll('.subnavigation__link').forEach((link) => link.classList.remove('subnavigation__link--active'))
      document
        .querySelectorAll('.subnavigation-container__a-b-button')
        .forEach((link) => link.classList.remove('subnavigation-container__a-b-button--active'))
    }

    if (link.classList.contains('header-navigation__link')) {
      link
        .closest('.header-navigation')
        .querySelectorAll('.header-navigation__link')
        .forEach((link) => link.classList.remove('header-navigation__link--active'))
      link.classList.add('header-navigation__link--active')
    }

    if (link.classList.contains('search-page__category')) {
      link
        .closest('.search-page__categories')
        .querySelectorAll('.search-page__category')
        .forEach((link) => link.classList.remove('search-page__category--active'))
      link.classList.add('search-page__category--active')
    }

    if (link.classList.contains('subnavigation__link')) {
      link
        .closest('.subnavigation')
        .querySelectorAll('.subnavigation__link')
        .forEach((link) => link.classList.remove('subnavigation__link--active'))
      link.classList.add('subnavigation__link--active')
    }

    if (link.classList.contains('subnavigation-container__a-b-button')) {
      link
        .closest('.subnavigation-container')
        .querySelectorAll('.subnavigation-container__a-b-button')
        .forEach((link) => link.classList.remove('subnavigation-container__a-b-button--active'))
      link.classList.add('subnavigation-container__a-b-button--active')
    }

    // this.el.links.mobile.forEach((link) => {
    //   link.classList.remove('mobile-navigation__link--active')
    //   link.classList.remove('mobile-navigation__link--current')
    // })

    // link.classList.contains('mobile-navigation__link') && link.classList.add('mobile-navigation__link--active')
  },

  handleAnnualReportReveal(delay): void {
    const banner = document.querySelector('.banner-ar:not(.banner-ar--no-image)')
    const hero = document.querySelector('.hero-ar')

    const bannerAboveViewport = () => {
      if (!banner) {
        return true
      }

      return banner.getBoundingClientRect().top * -1 > banner.getBoundingClientRect().height
    }

    const heroAboveViewport = () => hero.getBoundingClientRect().top * -1 > hero.getBoundingClientRect().height

    const loadBanner = () => {
      gsap
        .timeline({ defaults: { ease: 'power3.out' } })
        .set('.banner-ar', { opacity: 1 })
        .set('.banner-ar__picture', { opacity: 0 })
        .set('.banner-ar__image', { scale: 1.2 })
        .set('.banner-ar__heading, .banner-ar__title', { opacity: 0, y: 32 })
        .to('.banner-ar__picture', { duration: 2, ease: 'power3.inOut', opacity: 1 }, delay)
        .to('.banner-ar__image', { duration: 2, scale: 1, opacity: 1 }, '<')
        .to('.banner-ar__heading, .banner-ar__title', { duration: 0.765, opacity: 1, stagger: 0.1, y: 0 }, '<0.3')
    }

    const loadHero = () => {
      gsap
        .timeline({ defaults: { ease: 'power3.out' } })
        .set('.hero-ar', { opacity: 1 })
        .set('.hero-ar__picture', { opacity: 0 })
        .set('.hero-ar__image', { scale: 1.2 })
        .set('.hero-ar__heading, .hero-ar__title, .hero-ar__buttons .button', { opacity: 0, y: 32 })
        .to('.hero-ar__picture', { duration: 2, ease: 'power3.inOut', opacity: 1 }, delay)
        .to('.hero-ar__image', { duration: 2, scale: 1, opacity: 1 }, '<')
        .to('.hero-ar__heading, .hero-ar__title, .hero-ar__buttons .button', { duration: 0.765, opacity: 1, stagger: 0.1, y: 0 }, '<0.3')
    }

    banner && loadBanner()
    hero && loadHero()

    const revealDelay = () => {
      if (banner) {
        return delay + (bannerAboveViewport() ? 0 : 0.7)
      }

      if (hero) {
        return delay + (heroAboveViewport() ? 0 : 0.9)
      }

      return 0
    }

    revealOnLoad({
      callback: () => {
        gsap.matchMedia().add(gsapBreakpoints, (context) => {
          const { mobile } = context.conditions

          document.querySelectorAll(this.selectors.annualReport).forEach((item) => {
            revealOnScroll({ item, onEnterStart: mobile ? '0% 90%' : '0% 85%' })
          })
        })
      },
      delay: revealDelay(),
      selectors: this.selectors.annualReport,
    })
  },

  redirect(link): void {
    setTimeout(() => {
      window.location.href = link.getAttribute('href')
    }, 211)
  },
}

export default loader
